import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import categoryService from '../../../services/categoryService';
import '../../../pages/Pages.css';
import './Categories.css';
import { API_BASE_URL } from '../../../config/constants';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    image: '',
    sort_order: '',
    is_active: true
  });
  const [addFormData, setAddFormData] = useState({
    name: '',
    description: '',
    image: '',
    sort_order: '',
    is_active: true
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const response = await categoryService.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewCategory = async (categoryId) => {
    try {
      const response = await categoryService.getCategoryById(categoryId);
      setSelectedCategory(response.data);
      setShowViewModal(true);
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to load category details', 'error');
    }
  };

  const handleEditCategory = async (category) => {
    setSelectedCategory(category);
    setEditFormData({
      name: category.name,
      description: category.description || '',
      image: category.image || '',
      sort_order: category.sort_order || '',
      is_active: category.is_active
    });
    setImagePreview(null);
    setShowEditModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      await categoryService.updateCategory(selectedCategory.id, editFormData);
      Swal.fire('Success!', 'Category updated successfully', 'success');
      setShowEditModal(false);
      loadCategories();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to update category', 'error');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();

    if (!addFormData.name.trim()) {
      Swal.fire('Error!', 'Category name is required', 'error');
      return;
    }

    try {
      const categoryData = {
        name: addFormData.name,
        description: addFormData.description,
        image: addFormData.image,
        sort_order: addFormData.sort_order ? parseInt(addFormData.sort_order) : 0,
        is_active: addFormData.is_active
      };

      await categoryService.createCategory(categoryData);
      Swal.fire('Success!', 'Category created successfully', 'success');
      setShowAddModal(false);
      resetAddForm();
      loadCategories();
    } catch (error) {
      Swal.fire('Error!', error.message || 'Failed to create category', 'error');
    }
  };

  const resetAddForm = () => {
    setAddFormData({
      name: '',
      description: '',
      image: '',
      sort_order: '',
      is_active: true
    });
    setImagePreview(null);
  };

  const handleDeleteCategory = (category) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete "${category.name}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await categoryService.deleteCategory(category.id);
          Swal.fire('Deleted!', 'Category has been deleted.', 'success');
          loadCategories();
        } catch (error) {
          Swal.fire('Error!', error.message || 'Failed to delete category', 'error');
        }
      }
    });
  };

  const handleStatusUpdate = (category) => {
    const newStatus = !category.is_active;
    const action = newStatus ? 'activate' : 'deactivate';

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to ${action} "${category.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus ? '#3085d6' : '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: `Yes, ${action} it!`
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await categoryService.updateCategoryStatus(category.id, newStatus);
          Swal.fire('Updated!', `Category has been ${action}d.`, 'success');
          loadCategories();
        } catch (error) {
          Swal.fire('Error!', error.message || `Failed to ${action} category`, 'error');
        }
      }
    });
  };

  const handleImageChange = (e, formType) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        if (formType === 'add') {
          setAddFormData({ ...addFormData, image: reader.result });
        } else {
          setEditFormData({ ...editFormData, image: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder-image.jpg';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath;
    return `${process.env.REACT_APP_API_URL || API_BASE_URL}storage/${imagePath}`;
  };

  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCategories = [...filteredCategories].sort((a, b) => {
    if (a[sortConfig.key] < b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? -1 : 1;
    }
    if (a[sortConfig.key] > b[sortConfig.key]) {
      return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕️';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  if (loading) {
    return (
      <div className="cat-page-wrapper">
        <div className="cat-page-header">
          <h1 className="cat-page-title">Categories</h1>
          <p className="cat-page-subtitle">Manage product categories</p>
        </div>
        <div className="cat-loading-state">
          <div className="cat-loading-spinner"></div>
          <p>Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="cat-page-wrapper">
      <div className="cat-page-header">
        <div className="cat-header-content">
          <div className="cat-header-text">
            <h1 className="cat-page-title">Categories</h1>
            <p className="cat-page-subtitle">
              Manage your product categories ({filteredCategories.length} of {categories.length} categories)
            </p>
          </div>
          <div className="cat-header-actions">
            <div className="cat-search-box">
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cat-search-input"
              />
              <span className="cat-search-icon">🔍</span>
            </div>
            <button className="cat-btn cat-btn-secondary" onClick={loadCategories}>
              <span className="cat-btn-icon">🔄</span>
              Refresh
            </button>
            <button
              className="cat-btn cat-btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <span className="cat-btn-icon">➕</span>
              Add Category
            </button>
          </div>
        </div>
      </div>

      <div className="cat-page-content">
        {sortedCategories.length > 0 ? (
          <div className="cat-table-container">
            <table className="cat-table">
              <thead className="cat-table-header">
                <tr>
                  <th className="cat-table-th cat-table-image">Image</th>
                  <th 
                    className="cat-table-th cat-table-name sortable"
                    onClick={() => handleSort('name')}
                  >
                    <div className="cat-table-sort-header">
                      Name
                      <span className="cat-sort-icon">{getSortIcon('name')}</span>
                    </div>
                  </th>
                  <th className="cat-table-th cat-table-description">Description</th>
                  <th 
                    className="cat-table-th cat-table-sort-order sortable"
                    onClick={() => handleSort('sort_order')}
                  >
                    <div className="cat-table-sort-header">
                      Sort Order
                      <span className="cat-sort-icon">{getSortIcon('sort_order')}</span>
                    </div>
                  </th>
                  <th 
                    className="cat-table-th cat-table-status sortable"
                    onClick={() => handleSort('is_active')}
                  >
                    <div className="cat-table-sort-header">
                      Status
                      <span className="cat-sort-icon">{getSortIcon('is_active')}</span>
                    </div>
                  </th>
                  <th className="cat-table-th cat-table-subcategories">Subcategories</th>
                  <th 
                    className="cat-table-th cat-table-date sortable"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="cat-table-sort-header">
                      Created
                      <span className="cat-sort-icon">{getSortIcon('created_at')}</span>
                    </div>
                  </th>
                  <th className="cat-table-th cat-table-actions">Actions</th>
                </tr>
              </thead>
              <tbody className="cat-table-body">
                {sortedCategories.map(category => (
                  <tr key={category.id} className="cat-table-row">
                    <td className="cat-table-td cat-table-image-cell">
                      <div className="cat-table-image-wrapper">
                        <img
                          src={getImageUrl(category.image)}
                          alt={category.name}
                          className="cat-table-img"
                          onError={(e) => {
                            e.target.src = '/placeholder-image.jpg';
                          }}
                        />
                      </div>
                    </td>
                    <td className="cat-table-td cat-table-name-cell">
                      <div className="cat-table-name-content">
                        <span className="cat-table-name">{category.name}</span>
                        <span className="cat-table-slug">{category.slug}</span>
                      </div>
                    </td>
                    <td className="cat-table-td cat-table-description-cell">
                      <div className="cat-table-description">
                        {category.description || (
                          <span className="cat-no-description">No description</span>
                        )}
                      </div>
                    </td>
                    <td className="cat-table-td cat-table-sort-order-cell">
                      <span className="cat-sort-order-badge">{category.sort_order || 0}</span>
                    </td>
                    <td className="cat-table-td cat-table-status-cell">
                      <div className="cat-status-container">
                        <span className={`cat-status-badge ${category.is_active ? 'cat-status-active' : 'cat-status-inactive'}`}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="cat-table-td cat-table-subcategories-cell">
                      <div className="cat-subcategories-count">
                        <span className="cat-subcategories-number">
                          {category.sub_categories?.length || 0}
                        </span>
                        <span className="cat-subcategories-label">subcategories</span>
                      </div>
                    </td>
                    <td className="cat-table-td cat-table-date-cell">
                      {formatDate(category.created_at)}
                    </td>
                    <td className="cat-table-td cat-table-actions-cell">
                      <div className="cat-table-actions">
                        <button
                          className="cat-table-btn cat-table-btn-view"
                          onClick={() => handleViewCategory(category.id)}
                          title="View Details"
                        >
                          👁️
                        </button>
                        <button
                          className="cat-table-btn cat-table-btn-edit"
                          onClick={() => handleEditCategory(category)}
                          title="Edit Category"
                        >
                          ✏️
                        </button>
                        <button
                          className={`cat-table-btn ${category.is_active ? 'cat-table-btn-deactivate' : 'cat-table-btn-activate'}`}
                          onClick={() => handleStatusUpdate(category)}
                          title={category.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {category.is_active ? '⏸️' : '▶️'}
                        </button>
                        <button
                          className="cat-table-btn cat-table-btn-delete"
                          onClick={() => handleDeleteCategory(category)}
                          title="Delete Category"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cat-empty-state">
            <div className="cat-empty-icon">📁</div>
            <h3 className="cat-empty-title">No Categories Found</h3>
            <p className="cat-empty-description">
              {searchTerm ? 'No categories match your search. Try different keywords.' : 'Get started by creating your first category.'}
            </p>
            <button
              className="cat-btn cat-btn-primary cat-empty-action"
              onClick={() => setShowAddModal(true)}
            >
              <span className="cat-btn-icon">➕</span>
              Add New Category
            </button>
          </div>
        )}
      </div>

      {/* View Category Modal */}
      {showViewModal && selectedCategory && (
        <div className="cat-modal-overlay">
          <div className="cat-modal cat-modal-large">
            <div className="cat-modal-header">
              <h3 className="cat-modal-title">Category Details</h3>
              <button className="cat-modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="cat-modal-body">
              <div className="cat-detail-view">
                <div className="cat-detail-image-section">
                  <img
                    src={getImageUrl(selectedCategory.image)}
                    alt={selectedCategory.name}
                    className="cat-detail-image"
                    onError={(e) => {
                      e.target.src = '/placeholder-image.jpg';
                    }}
                  />
                </div>
                <div className="cat-detail-grid">
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Name</label>
                    <span className="cat-detail-value">{selectedCategory.name}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Slug</label>
                    <span className="cat-detail-value cat-detail-slug">{selectedCategory.slug}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Description</label>
                    <span className="cat-detail-value">{selectedCategory.description || 'No description'}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Sort Order</label>
                    <span className="cat-detail-value">{selectedCategory.sort_order}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Status</label>
                    <span className={`cat-status-badge ${selectedCategory.is_active ? 'cat-status-active' : 'cat-status-inactive'}`}>
                      {selectedCategory.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Sub Categories</label>
                    <span className="cat-detail-value">{selectedCategory.sub_categories?.length || 0}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Created</label>
                    <span className="cat-detail-value">{formatDate(selectedCategory.created_at)}</span>
                  </div>
                  <div className="cat-detail-item">
                    <label className="cat-detail-label">Updated</label>
                    <span className="cat-detail-value">{formatDate(selectedCategory.updated_at)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="cat-modal-footer">
              <button className="cat-btn cat-btn-secondary" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && (
        <div className="cat-modal-overlay">
          <div className="cat-modal cat-modal-large">
            <div className="cat-modal-header">
              <h3 className="cat-modal-title">Edit Category</h3>
              <button className="cat-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateCategory}>
              <div className="cat-modal-body">
                <div className="cat-form-grid">
                  <div className="cat-form-group">
                    <label className="cat-form-label">Category Name *</label>
                    <input
                      type="text"
                      value={editFormData.name}
                      onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                      className="cat-form-input"
                      required
                    />
                  </div>
                  <div className="cat-form-group">
                    <label className="cat-form-label">Sort Order</label>
                    <input
                      type="number"
                      value={editFormData.sort_order}
                      onChange={(e) => setEditFormData({ ...editFormData, sort_order: e.target.value })}
                      className="cat-form-input"
                      min="0"
                    />
                  </div>
                  <div className="cat-form-group cat-form-full">
                    <label className="cat-form-label">Description</label>
                    <textarea
                      value={editFormData.description}
                      onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                      className="cat-form-textarea"
                      rows="3"
                      placeholder="Enter category description..."
                    />
                  </div>
                  <div className="cat-form-group cat-form-full">
                    <label className="cat-form-label">Category Image</label>
                    <div className="cat-image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'edit')}
                        className="cat-image-input"
                      />
                      <div className="cat-image-preview-container">
                        {(imagePreview || editFormData.image) && (
                          <div className="cat-image-preview">
                            <img
                              src={imagePreview || getImageUrl(editFormData.image)}
                              alt="Preview"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cat-form-group">
                    <label className="cat-form-label">Status</label>
                    <div className="cat-checkbox-wrapper">
                      <label className="cat-checkbox-label">
                        <input
                          type="checkbox"
                          checked={editFormData.is_active}
                          onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                          className="cat-checkbox-input"
                        />
                        <span className="cat-checkbox-custom"></span>
                        Active Category
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cat-modal-footer">
                <button type="button" className="cat-btn cat-btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="cat-btn cat-btn-primary">
                  Update Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="cat-modal-overlay">
          <div className="cat-modal cat-modal-large">
            <div className="cat-modal-header">
              <h3 className="cat-modal-title">Add New Category</h3>
              <button className="cat-modal-close" onClick={() => { setShowAddModal(false); resetAddForm(); }}>×</button>
            </div>
            <form onSubmit={handleAddCategory}>
              <div className="cat-modal-body">
                <div className="cat-form-grid">
                  <div className="cat-form-group">
                    <label className="cat-form-label">Category Name *</label>
                    <input
                      type="text"
                      value={addFormData.name}
                      onChange={(e) => setAddFormData({ ...addFormData, name: e.target.value })}
                      className="cat-form-input"
                      required
                      placeholder="Enter category name"
                    />
                  </div>
                  <div className="cat-form-group">
                    <label className="cat-form-label">Sort Order</label>
                    <input
                      type="number"
                      value={addFormData.sort_order}
                      onChange={(e) => setAddFormData({ ...addFormData, sort_order: e.target.value })}
                      className="cat-form-input"
                      min="0"
                      placeholder="0"
                    />
                  </div>
                  <div className="cat-form-group cat-form-full">
                    <label className="cat-form-label">Description</label>
                    <textarea
                      value={addFormData.description}
                      onChange={(e) => setAddFormData({ ...addFormData, description: e.target.value })}
                      className="cat-form-textarea"
                      rows="3"
                      placeholder="Enter category description..."
                    />
                  </div>
                  <div className="cat-form-group cat-form-full">
                    <label className="cat-form-label">Category Image</label>
                    <div className="cat-image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageChange(e, 'add')}
                        className="cat-image-input"
                      />
                      <div className="cat-image-preview-container">
                        {imagePreview && (
                          <div className="cat-image-preview">
                            <img src={imagePreview} alt="Preview" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="cat-form-group">
                    <label className="cat-form-label">Status</label>
                    <div className="cat-checkbox-wrapper">
                      <label className="cat-checkbox-label">
                        <input
                          type="checkbox"
                          checked={addFormData.is_active}
                          onChange={(e) => setAddFormData({ ...addFormData, is_active: e.target.checked })}
                          className="cat-checkbox-input"
                        />
                        <span className="cat-checkbox-custom"></span>
                        Active Category
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="cat-modal-footer">
                <button type="button" className="cat-btn cat-btn-secondary" onClick={() => { setShowAddModal(false); resetAddForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="cat-btn cat-btn-primary">
                  Create Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;